import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CERTIFICATE_IMPORTS_PATH = "/settings/certificate-imports" as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SCHEDULED_INVITES_PATH = "/settings/scheduled-invites" as any;
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/lib/ui/Collapsible";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from "@src/lib/ui/Sidebar";
import GearOutlineIcon from "@src/lib/icons/18px/gear-outline";
import {
  TooltipMenu,
  TooltipMenuContent,
  TooltipMenuHeader,
  TooltipMenuItem,
  TooltipMenuTrigger,
} from "@src/lib/ui/TooltipMenu";
import { useBillingInitialized } from "@src/features/settings/hooks/billing/useBillingInitialized";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useUser from "@src/features/app/hooks/useUser";
import { UserType } from "@src/types.generated";

const AdminLeftNavSettingsMenuButton: React.FC = () => {
  const { pathname } = useRouter();
  const { state } = useSidebar();
  const { userActionPermissions } = useActionPermissions();
  const { user } = useUser();
  // Scheduled invites is an admin-only feature at the backend (UserActor +
  // resolver reject non-admins) even though inviteOthers can be granted to
  // managers. Gate the nav on both to avoid a broken link for managers.
  const canSeeScheduledInvites =
    !!userActionPermissions?.inviteOthers && user?.userType === UserType.Admin;
  const billingInitialized = useBillingInitialized({
    skip: !userActionPermissions?.manageBilling,
  });
  const SettingsMenuButton = React.useMemo(
    () => (
      <SidebarMenuButton asChild>
        <Link href="/settings">
          <GearOutlineIcon />
          Settings
        </Link>
      </SidebarMenuButton>
    ),
    [],
  );
  return (
    <SidebarMenuItem className="relative">
      {state === "collapsed" ? (
        <TooltipMenu>
          <TooltipMenuTrigger asChild>{SettingsMenuButton}</TooltipMenuTrigger>
          <TooltipMenuContent side="right" align="center">
            <TooltipMenuHeader>Settings</TooltipMenuHeader>
            {userActionPermissions?.changeOrgSettings ? (
              <>
                <TooltipMenuItem>
                  <Link href="/settings/ask-opus">Ask Opus</Link>
                </TooltipMenuItem>
                <TooltipMenuItem>
                  <Link href="/settings">Brand</Link>
                </TooltipMenuItem>
                <TooltipMenuItem>
                  <Link href="/settings/compliance">Wage & Hour</Link>
                </TooltipMenuItem>
                <TooltipMenuItem>
                  <Link href="/settings/language">Languages</Link>
                </TooltipMenuItem>
              </>
            ) : null}
            {billingInitialized && userActionPermissions?.manageBilling ? (
              <TooltipMenuItem>
                <Link href="/settings/billing">Billing</Link>
              </TooltipMenuItem>
            ) : null}
            {userActionPermissions?.manageOrgIntegrations ? (
              <>
                <TooltipMenuItem>
                  <Link href="/settings/training-imports">
                    Training Imports
                  </Link>
                </TooltipMenuItem>
                <TooltipMenuItem>
                  <Link href={CERTIFICATE_IMPORTS_PATH}>
                    Certificate Imports
                  </Link>
                </TooltipMenuItem>
              </>
            ) : null}
            {canSeeScheduledInvites ? (
              <TooltipMenuItem>
                <Link href={SCHEDULED_INVITES_PATH}>Scheduled Invites</Link>
              </TooltipMenuItem>
            ) : null}
          </TooltipMenuContent>
        </TooltipMenu>
      ) : (
        <Collapsible
          className="group/collapsible group/settings"
          open={
            pathname.startsWith("/settings") && pathname !== "/settings/archive"
          }
        >
          <CollapsibleTrigger asChild>{SettingsMenuButton}</CollapsibleTrigger>
          <CollapsibleContent>
            {userActionPermissions?.changeOrgSettings ? (
              <>
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    asChild
                    isActive={pathname === "/settings/ask-opus"}
                  >
                    <Link href="/settings/ask-opus">Ask Opus</Link>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    asChild
                    isActive={pathname === "/settings/brand"}
                  >
                    <Link href="/settings/brand">Brand</Link>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    asChild
                    isActive={pathname === "/settings/compliance"}
                  >
                    <Link href="/settings/compliance">Wage & Hour</Link>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    asChild
                    isActive={pathname === "/settings/language"}
                  >
                    <Link href="/settings/language">Languages</Link>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </>
            ) : null}
            {billingInitialized && userActionPermissions?.manageBilling ? (
              <SidebarMenuSub>
                <SidebarMenuSubItem
                  asChild
                  isActive={pathname === "/settings/billing"}
                >
                  <Link href="/settings/billing">Billing</Link>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            ) : null}
            {userActionPermissions?.manageOrgIntegrations ? (
              <>
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    asChild
                    isActive={pathname === "/settings/training-imports"}
                  >
                    <Link href="/settings/training-imports">
                      Training Imports
                    </Link>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    asChild
                    isActive={pathname === CERTIFICATE_IMPORTS_PATH}
                  >
                    <Link href={CERTIFICATE_IMPORTS_PATH}>
                      Certificate Imports
                    </Link>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </>
            ) : null}
            {canSeeScheduledInvites ? (
              <SidebarMenuSub>
                <SidebarMenuSubItem
                  asChild
                  isActive={pathname === SCHEDULED_INVITES_PATH}
                >
                  <Link href={SCHEDULED_INVITES_PATH}>Scheduled Invites</Link>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            ) : null}
          </CollapsibleContent>
        </Collapsible>
      )}
    </SidebarMenuItem>
  );
};

export default AdminLeftNavSettingsMenuButton;
